/**
 * Template Validation Utilities
 * 
 * Comprehensive validation for notification templates including:
 * - Variable validation (available vs used)
 * - Required variable checking
 * - Content validation
 * - Variable extraction and analysis
 */

/**
 * Extract all variables from template content
 * @param {Object} content - Template content object
 * @returns {Array} Array of variable names found in content
 */
export const extractVariablesFromContent = (content) => {
  const variables = new Set();
  const variableRegex = /\{\{([^}]+)\}\}/g;
  
  // Check all content fields
  const contentFields = [
    content.subject,
    content.bodyHtml,
    content.bodyText,
    content.inAppTitle,
    content.inAppMessage
  ];
  
  contentFields.forEach(field => {
    if (typeof field === 'string') {
      let match;
      while ((match = variableRegex.exec(field)) !== null) {
        variables.add(match[1].trim());
      }
    }
  });
  
  return Array.from(variables);
};

/**
 * Validate template variables against available schema
 * @param {Object} template - Template object
 * @param {string} languageCode - Language code to validate
 * @returns {Object} Validation result with errors and warnings
 */
export const validateTemplateVariables = (template, languageCode = 'en') => {
  const errors = [];
  const warnings = [];
  
  // Find the language content
  const languageContent = template.languages?.find(lang => lang.code === languageCode);
  if (!languageContent) {
    errors.push(`No content found for language: ${languageCode}`);
    return { isValid: false, errors, warnings };
  }
  
  // Extract variables from content
  const usedVariables = extractVariablesFromContent(languageContent.content);
  const availableVariables = template.variables?.available || [];
  const requiredVariables = template.variables?.required || [];
  
  // Check for unknown variables (used but not available)
  const unknownVariables = usedVariables.filter(variable => 
    !availableVariables.includes(variable)
  );
  
  unknownVariables.forEach(variable => {
    errors.push(`Unknown variable: {{${variable}}}. Available variables: ${availableVariables.join(', ')}`);
  });
  
  // Check for missing required variables (make this a warning instead of error for better UX)
  const missingRequired = requiredVariables.filter(variable => 
    !usedVariables.includes(variable)
  );
  
  missingRequired.forEach(variable => {
    // Only treat club.name as a hard requirement, others as warnings
    if (variable === 'club.name') {
      errors.push(`Required variable missing: {{${variable}}}`);
    } else {
      warnings.push(`Recommended variable missing: {{${variable}}} - Consider including this for better personalization`);
    }
  });
  
  // Check for unused available variables (warning only)
  const unusedVariables = availableVariables.filter(variable => 
    !usedVariables.includes(variable) && !requiredVariables.includes(variable)
  );
  
  if (unusedVariables.length > 0) {
    warnings.push(`Unused available variables: ${unusedVariables.map(v => `{{${v}}}`).join(', ')}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    usedVariables,
    availableVariables,
    requiredVariables,
    unknownVariables,
    missingRequired
  };
};

/**
 * Validate template content requirements
 * @param {Object} content - Language content object
 * @returns {Object} Content validation result
 */
export const validateTemplateContent = (content) => {
  const errors = [];
  const warnings = [];
  
  // Required fields
  if (!content.inAppTitle?.trim()) {
    errors.push('In-app notification title is required');
  }
  
  if (!content.inAppMessage?.trim()) {
    errors.push('In-app notification message is required');
  }
  
  // Optional but recommended fields
  if (!content.subject?.trim()) {
    warnings.push('Email subject is recommended for email notifications');
  }
  
  if (!content.bodyHtml?.trim() && !content.bodyText?.trim()) {
    warnings.push('Email content (HTML or text) is recommended for email notifications');
  }
  
  // Content length validation
  if (content.inAppTitle && content.inAppTitle.length > 100) {
    warnings.push('In-app title is quite long (>100 chars). Consider shortening for better mobile display.');
  }
  
  if (content.inAppMessage && content.inAppMessage.length > 200) {
    warnings.push('In-app message is quite long (>200 chars). Consider shortening for better mobile display.');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Comprehensive template validation
 * @param {Object} template - Complete template object
 * @param {string} languageCode - Language to validate
 * @returns {Object} Complete validation result
 */
export const validateTemplate = (template, languageCode = 'en') => {
  const variableValidation = validateTemplateVariables(template, languageCode);
  
  const languageContent = template.languages?.find(lang => lang.code === languageCode);
  const contentValidation = languageContent ? 
    validateTemplateContent(languageContent.content) : 
    { isValid: false, errors: ['No content found'], warnings: [] };
  
  return {
    isValid: variableValidation.isValid && contentValidation.isValid,
    errors: [...variableValidation.errors, ...contentValidation.errors],
    warnings: [...variableValidation.warnings, ...contentValidation.warnings],
    variableAnalysis: {
      used: variableValidation.usedVariables,
      available: variableValidation.availableVariables,
      required: variableValidation.requiredVariables,
      unknown: variableValidation.unknownVariables,
      missingRequired: variableValidation.missingRequired
    }
  };
};

/**
 * Generate sample data for template testing
 * @param {Array} availableVariables - List of available variables
 * @param {string} templateType - Template type for context-specific data
 * @returns {Object} Sample data object
 */
export const generateSampleData = (availableVariables, templateType = '') => {
  const sampleData = {};
  
  // Default sample values
  const defaultValues = {
    // User variables
    'user.firstName': 'Alex',
    'user.lastName': 'Johnson',
    'user.fullName': 'Alex Johnson',
    'user.email': 'alex@example.com',
    
    // Club variables
    'club.name': 'Thunder Soccer Club',
    'club.description': 'Premier youth soccer club',
    'club.role': 'Player',
    'club.newRole': 'Team Captain',
    'club.previousRole': 'Player',
    'club.joinDate': new Date().toLocaleDateString(),
    'club.membershipType': 'Premium Member',
    
    // Applicant variables (for join requests)
    'applicant.firstName': 'Sarah',
    'applicant.lastName': 'Wilson',
    'applicant.fullName': 'Sarah Wilson',
    'applicant.email': 'sarah@example.com',
    'applicant.requestedRole': 'Player',
    
    // Payment variables
    'payment.amount': '$150.00',
    'payment.dueDate': new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'payment.date': new Date().toLocaleDateString(),
    'payment.description': 'Monthly membership fee',
    'payment.transactionId': 'TXN-' + Math.random().toString(36).substr(2, 9).toUpperCase(),
    
    // Program variables
    'program.name': 'Youth Soccer League',
    'program.startDate': new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'program.endDate': new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'program.schedule': 'Tuesdays and Thursdays, 6-8 PM',
    'program.location': 'Central Sports Complex',
    
    // Event variables
    'event.name': 'Championship Final',
    'event.title': 'Championship Final',
    'event.date': new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'event.time': '3:00 PM',
    'event.location': 'Stadium Field A',
    'event.description': 'Final match of the season',
    
    // Equipment variables
    'equipment.name': 'Soccer Ball Set',
    'equipment.type': 'Training Equipment',
    'equipment.quantity': '5',
    'equipment.condition': 'Good',
    
    // Team variables
    'team.name': 'Thunder U16',
    'coach.name': 'Coach Smith',
    'playerRole': 'Midfielder',
    'practice.date': new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'practice.time': '6:00 PM',
    
    // Assignment variables
    'assignment.title': 'Equipment Inventory',
    'assignment.description': 'Count all soccer balls and cones',
    'assignment.dueDate': new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'assignment.priority': 'High',
    
    // RSVP variables
    'rsvp.status': 'Attending',
    'rsvp.deadline': new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'rsvp.count': '25',
    
    // System variables
    'maintenance.startTime': '2:00 AM EST',
    'maintenance.duration': '2 hours',
    'security.action': 'Password reset',
    
    // Other variables
    'assignedBy.name': 'Admin Johnson',
    'rejection.reason': 'Club capacity reached',
    'removal.date': new Date().toLocaleDateString(),
    'removal.reason': 'Membership expired',
    'returnDate': new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString(),
    'weather.alert': 'Rain expected - game may be postponed',
    
    // Common variables
    'currentDateTime': new Date().toLocaleString(),
    'actionUrl': '/dashboard',
    'actionLabel': 'View Details',
    'message': 'This is a sample notification message'
  };
  
  // Generate sample data for available variables
  availableVariables.forEach(variable => {
    if (defaultValues[variable]) {
      sampleData[variable] = defaultValues[variable];
    } else {
      // Generate a generic sample value
      sampleData[variable] = `Sample ${variable.replace(/\./g, ' ')}`;
    }
  });
  
  return sampleData;
};

/**
 * Render template with sample data
 * @param {string} templateString - Template string with variables
 * @param {Object} sampleData - Sample data object
 * @returns {string} Rendered template
 */
export const renderTemplateWithSampleData = (templateString, sampleData) => {
  if (!templateString) return '';
  
  let rendered = templateString;
  
  // Replace variables with sample data
  Object.entries(sampleData).forEach(([variable, value]) => {
    const regex = new RegExp(`\\{\\{\\s*${variable.replace(/\./g, '\\.')}\\s*\\}\\}`, 'g');
    rendered = rendered.replace(regex, value);
  });
  
  return rendered;
};

/**
 * Suggest variable corrections for unknown variables
 * @param {string} unknownVariable - Unknown variable name
 * @param {Array} availableVariables - Available variables
 * @returns {Array} Suggested corrections
 */
export const suggestVariableCorrections = (unknownVariable, availableVariables) => {
  const suggestions = [];
  
  // Simple string similarity check
  availableVariables.forEach(available => {
    const similarity = calculateStringSimilarity(unknownVariable, available);
    if (similarity > 0.6) {
      suggestions.push({
        variable: available,
        similarity,
        suggestion: `Did you mean {{${available}}}?`
      });
    }
  });
  
  // Sort by similarity
  return suggestions.sort((a, b) => b.similarity - a.similarity);
};

/**
 * Calculate string similarity (simple Levenshtein-based)
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score (0-1)
 */
const calculateStringSimilarity = (str1, str2) => {
  const longer = str1.length > str2.length ? str1 : str2;
  const shorter = str1.length > str2.length ? str2 : str1;
  
  if (longer.length === 0) return 1.0;
  
  const editDistance = levenshteinDistance(longer, shorter);
  return (longer.length - editDistance) / longer.length;
};

/**
 * Calculate Levenshtein distance between two strings
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance
 */
const levenshteinDistance = (str1, str2) => {
  const matrix = [];
  
  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }
  
  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }
  
  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }
  
  return matrix[str2.length][str1.length];
};
